import React, {
  Fragment,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react'
import { Box, Grid2 as Grid, Tab, Tabs, Typography } from '@mui/material'
import { Modal } from '../../../components/_EOS/Modal'
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { GenericTableV2 } from '../../../components/_EOS/GenericTable'
import { CourierServiceHeaderFields } from './components/CourierServiceHeaderFields'
import { ExceptionCard } from './components/ExceptionCard'
import { NewExceptionForm } from './components/NewExceptionForm'
import { WeeklyDayCard } from './components/WeeklyDayCard'
import { getCourierServiceSchedule, putCourierServiceSchedule } from '../config/api'
import {
  CourierCollectionPlannerFormEditProps,
  CourierCollectionPlannerFormEditRef,
  CourierServiceScheduleDetailDto,
  CourierServiceScheduleSaveDto,
  createPickupTimeActions,
  createPickupTimeColumns,
  CUT_OFF_MINUTES_MAX,
  ExceptionScheduleDto,
  ExceptionScheduleSaveDto,
  isCutOffMinutesValid,
  isMaxPackagesCapacityValid,
  isMaxUnitsCapacityValid,
  PickupTimeRow,
  PickupTimeSaveDto,
  WEEK_DAYS,
  WeeklyScheduleDto,
  WeeklyScheduleSaveDto
} from '../config/config'
import { formStyles } from './styles'

const createEmptyWeeklySchedules = (): WeeklyScheduleSaveDto[] =>
  WEEK_DAYS.map((day) => ({
    dayId: day.dayId,
    active: false,
    pickupTimes: []
  }))

const mapWeeklySchedulesFromApi = (
  weeklySchedules: WeeklyScheduleDto[]
): WeeklyScheduleSaveDto[] =>
  WEEK_DAYS.map((day) => {
    const apiDay = weeklySchedules.find((schedule) => schedule.dayId === day.dayId)
    return {
      dayId: day.dayId,
      active: apiDay?.active ?? false,
      pickupTimes:
        apiDay?.pickupTimes.map((pickupTime) => ({
          pickupTime: pickupTime.pickupTime.slice(0, 5),
          cutOffMinutes: pickupTime.cutOffMinutes
        })) ?? []
    }
  })

const mapExceptionsFromApi = (
  exceptions: ExceptionScheduleDto[]
): ExceptionScheduleSaveDto[] =>
  exceptions.map((exception) => ({
    description: exception.description,
    exceptionDate: exception.exceptionDate ? exception.exceptionDate.slice(0, 10) : '',
    active: exception.active,
    pickupTimes: exception.pickupTimes.map((pickupTime) => ({
      pickupTime: pickupTime.pickupTime.slice(0, 5),
      cutOffMinutes: pickupTime.cutOffMinutes
    }))
  }))

const formatExceptionDate = (dateStr: string) => {
  if (!dateStr) return ''
  const date = new Date(`${dateStr}T00:00:00`)
  return new Intl.DateTimeFormat('es-CL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date)
}

export const CourierCollectionPlannerFormEdit = forwardRef<
  CourierCollectionPlannerFormEditRef,
  CourierCollectionPlannerFormEditProps
>(({ courier, onSave, onSavingChange, handleSnackbar }, ref) => {
  const { state: scheduleState, callApi: callScheduleApi } =
    useApi<CourierServiceScheduleDetailDto>()
  const { callApi: callSaveApi } = useApi()

  const [activeTab, setActiveTab] = useState<'weekly' | 'exceptions'>('weekly')
  const [maxUnitsCapacity, setMaxUnitsCapacity] = useState<string>(
    courier.maxUnitsCapacity?.toString() || ''
  )
  const [maxPackagesCapacity, setMaxPackagesCapacity] = useState<string>(
    courier.maxPackagesCapacity?.toString() || ''
  )
  const isMaxUnitsCapacityInvalid = !isMaxUnitsCapacityValid(maxUnitsCapacity)
  const isMaxPackagesCapacityInvalid = !isMaxPackagesCapacityValid(maxPackagesCapacity)
  const [weeklySchedules, setWeeklySchedules] = useState<WeeklyScheduleSaveDto[]>(
    createEmptyWeeklySchedules()
  )
  const [exceptions, setExceptions] = useState<ExceptionScheduleSaveDto[]>([])
  const [newExceptionDate, setNewExceptionDate] = useState('')
  const [newExceptionDescription, setNewExceptionDescription] = useState('')
  const [expandedDays, setExpandedDays] = useState<Record<number, boolean>>(
    () => Object.fromEntries(WEEK_DAYS.map((day) => [day.dayId, true]))
  )
  const [isSaving, setIsSaving] = useState(false)
  const [showTabErrorDialog, setShowTabErrorDialog] = useState(false)

  const hasInvalidCutOffMinutes = weeklySchedules.some((day) =>
    day.pickupTimes.some((pickupTime) => !isCutOffMinutesValid(pickupTime.cutOffMinutes))
  )
  const hasInvalidExceptionCutOffMinutes = exceptions.some((exception) =>
    exception.pickupTimes.some((pickupTime) => !isCutOffMinutesValid(pickupTime.cutOffMinutes))
  )

  const handleTabChange = (value: 'weekly' | 'exceptions') => {
    if (isSaving) return
    if (activeTab === 'weekly' && value === 'exceptions' && hasInvalidCutOffMinutes) {
      setShowTabErrorDialog(true)
      return
    }
    if (activeTab === 'exceptions' && value === 'weekly' && hasInvalidExceptionCutOffMinutes) {
      setShowTabErrorDialog(true)
      return
    }
    setActiveTab(value)
  }

  const toggleDayExpanded = (dayId: number) => {
    if (isSaving) return
    setExpandedDays((prev) => ({ ...prev, [dayId]: !prev[dayId] }))
  }

  useEffect(() => {
    callScheduleApi(getCourierServiceSchedule(courier.courierServiceId))
      .then((response: CourierServiceScheduleDetailDto) => {
        const mappedSchedules = mapWeeklySchedulesFromApi(response.weeklySchedules)
        setWeeklySchedules(mappedSchedules)
        setExceptions(mapExceptionsFromApi(response.exceptions))
        setExpandedDays(
          Object.fromEntries(
            mappedSchedules.map((day) => [day.dayId, day.pickupTimes.length > 0])
          )
        )
      })
      .catch((error) => {
        console.log(error)
      })
  }, [courier.courierServiceId])

  const handleDayActiveChange = (dayId: number, active: boolean) => {
    setWeeklySchedules((prev) =>
      prev.map((day) => (day.dayId === dayId ? { ...day, active } : day))
    )
    setExpandedDays((prev) => ({ ...prev, [dayId]: active }))
  }

  const handleAddPickupTime = (dayId: number) => {
    setWeeklySchedules((prev) =>
      prev.map((day) =>
        day.dayId === dayId
          ? {
              ...day,
              pickupTimes: [
                ...day.pickupTimes,
                { pickupTime: '', cutOffMinutes: 0 }
              ]
            }
          : day
      )
    )
    setExpandedDays((prev) => ({ ...prev, [dayId]: true }))
  }

  const handleRemovePickupTime = (dayId: number, index: number) => {
    setWeeklySchedules((prev) =>
      prev.map((day) =>
        day.dayId === dayId
          ? { ...day, pickupTimes: day.pickupTimes.filter((_, i) => i !== index) }
          : day
      )
    )
  }

  const handlePickupTimeChange = (
    dayId: number,
    index: number,
    field: keyof PickupTimeSaveDto,
    value: string
  ) => {
    setWeeklySchedules((prev) =>
      prev.map((day) => {
        if (day.dayId !== dayId) return day
        return {
          ...day,
          pickupTimes: day.pickupTimes.map((pickupTime, i) =>
            i === index
              ? {
                  ...pickupTime,
                  [field]: field === 'cutOffMinutes' ? Number(value) : value
                }
              : pickupTime
          )
        }
      })
    )
  }

  const handleAddException = () => {
    if (!newExceptionDate || !newExceptionDescription.trim()) return

    setExceptions((prev) => [
      ...prev,
      {
        description: newExceptionDescription.trim(),
        exceptionDate: newExceptionDate,
        active: false,
        pickupTimes: []
      }
    ])
    setNewExceptionDate('')
    setNewExceptionDescription('')
  }

  const handleRemoveException = (index: number) => {
    setExceptions((prev) => prev.filter((_, i) => i !== index))
  }

  const handleExceptionActiveChange = (index: number, active: boolean) => {
    setExceptions((prev) =>
      prev.map((exception, i) => (i === index ? { ...exception, active } : exception))
    )
  }

  const handleAddExceptionPickupTime = (index: number) => {
    setExceptions((prev) =>
      prev.map((exception, i) =>
        i === index
          ? {
              ...exception,
              pickupTimes: [
                ...exception.pickupTimes,
                { pickupTime: '', cutOffMinutes: 0 }
              ]
            }
          : exception
      )
    )
  }

  const handleRemoveExceptionPickupTime = (index: number, pickupIndex: number) => {
    setExceptions((prev) =>
      prev.map((exception, i) =>
        i === index
          ? {
              ...exception,
              pickupTimes: exception.pickupTimes.filter((_, pi) => pi !== pickupIndex)
            }
          : exception
      )
    )
  }

  const handleExceptionPickupTimeChange = (
    index: number,
    pickupIndex: number,
    field: keyof PickupTimeSaveDto,
    value: string
  ) => {
    setExceptions((prev) =>
      prev.map((exception, i) => {
        if (i !== index) return exception
        return {
          ...exception,
          pickupTimes: exception.pickupTimes.map((pickupTime, pi) =>
            pi === pickupIndex
              ? {
                  ...pickupTime,
                  [field]: field === 'cutOffMinutes' ? Number(value) : value
                }
              : pickupTime
          )
        }
      })
    )
  }

  const renderPickupTimesTable = (
    pickupTimes: PickupTimeSaveDto[],
    onChangeTime: (index: number, field: keyof PickupTimeSaveDto, value: string) => void,
    onRemove: (index: number) => void,
    emptyMessage: string
  ) => (
    <GenericTableV2<PickupTimeRow>
      data={pickupTimes.map((pickupTime, index) => ({ ...pickupTime, id: index }))}
      keyField='id'
      noResultsComponent={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 2 }}>
          <InfoOutlinedIcon fontSize='small' sx={{ color: 'grey.400' }} />
          <Typography variant='caption' color='textSecondary' sx={{ fontSize: '0.875rem' }}>
            {emptyMessage}
          </Typography>
        </Box>
      }
      columns={createPickupTimeColumns(onChangeTime)}
      actions={createPickupTimeActions(onRemove, isSaving)}
    />
  )

  const handleSave = () => {
    if (isMaxUnitsCapacityInvalid) {
      handleSnackbar?.('La capacidad máxima de unidades debe ser mayor a 0.', false)
      return
    }

    if (isMaxPackagesCapacityInvalid) {
      handleSnackbar?.('La capacidad máxima de paquetes debe ser mayor a 0.', false)
      return
    }

    if (hasInvalidCutOffMinutes) {
      handleSnackbar?.(
        `El tiempo de corte debe ser mayor a 0 y no superar los ${CUT_OFF_MINUTES_MAX} minutos.`,
        false
      )
      return
    }

    const hasMissingPickupTimes = weeklySchedules.some(
      (day) => day.active && day.pickupTimes.length === 0
    )

    if (hasMissingPickupTimes) {
      handleSnackbar?.(
        'Cada día activo debe tener al menos un horario de recolección configurado.',
        false
      )
      return
    }

    const dto: CourierServiceScheduleSaveDto = {
      courierServiceId: courier.courierServiceId,
      maxUnitsCapacity: maxUnitsCapacity ? Number(maxUnitsCapacity) : null,
      maxPackagesCapacity: maxPackagesCapacity ? Number(maxPackagesCapacity) : null,
      weeklySchedules,
      exceptions
    }

    setIsSaving(true)
    onSavingChange?.(true)
    callSaveApi(putCourierServiceSchedule(dto))
      .then(() => {
        handleSnackbar?.('Horario del courier actualizado correctamente', true)
        onSave(true)
      })
      .catch((error) => {
        handleSnackbar?.(error, false)
      })
      .finally(() => {
        setIsSaving(false)
        onSavingChange?.(false)
      })
  }

  useImperativeHandle(ref, () => ({ save: handleSave }))

  if (scheduleState.status === Status.Loading) {
    return <Loading />
  }

  return (
    <Fragment>
      <Grid container spacing={2}>
      <CourierServiceHeaderFields
        courier={courier}
        maxUnitsCapacity={maxUnitsCapacity}
        maxPackagesCapacity={maxPackagesCapacity}
        isMaxUnitsCapacityInvalid={isMaxUnitsCapacityInvalid}
        isMaxPackagesCapacityInvalid={isMaxPackagesCapacityInvalid}
        onMaxUnitsCapacityChange={setMaxUnitsCapacity}
        onMaxPackagesCapacityChange={setMaxPackagesCapacity}
      />

      <Grid size={{ xs: 12 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, value) => handleTabChange(value)}
          sx={formStyles.tabs}
        >
          <Tab label='Horarios Semanales' value='weekly' disabled={isSaving} />
          <Tab label='Excepciones de Recolección' value='exceptions' disabled={isSaving} />
        </Tabs>
      </Grid>

      {activeTab === 'weekly' && (
        <Grid size={{ xs: 12 }}>
          {weeklySchedules.map((day) => (
            <WeeklyDayCard
              key={day.dayId}
              day={day}
              dayName={WEEK_DAYS.find((d) => d.dayId === day.dayId)?.name}
              isSaving={isSaving}
              expanded={expandedDays[day.dayId]}
              onToggleExpanded={toggleDayExpanded}
              onActiveChange={handleDayActiveChange}
              onAddPickupTime={handleAddPickupTime}
              onPickupTimeChange={handlePickupTimeChange}
              onRemovePickupTime={handleRemovePickupTime}
              renderPickupTimesTable={renderPickupTimesTable}
            />
          ))}
        </Grid>
      )}

      {activeTab === 'exceptions' && (
        <Grid size={{ xs: 12 }}>
          <NewExceptionForm
            newExceptionDate={newExceptionDate}
            newExceptionDescription={newExceptionDescription}
            isSaving={isSaving}
            onDateChange={setNewExceptionDate}
            onDescriptionChange={setNewExceptionDescription}
            onAdd={handleAddException}
          />

          {exceptions.map((exception, index) => (
            <ExceptionCard
              key={index}
              exception={exception}
              index={index}
              isSaving={isSaving}
              formatDate={formatExceptionDate}
              onActiveChange={handleExceptionActiveChange}
              onRemove={handleRemoveException}
              onAddPickupTime={handleAddExceptionPickupTime}
              onPickupTimeChange={handleExceptionPickupTimeChange}
              onRemovePickupTime={handleRemoveExceptionPickupTime}
              renderPickupTimesTable={renderPickupTimesTable}
            />
          ))}
        </Grid>
      )}
      </Grid>

      <Modal
        open={showTabErrorDialog}
        onClose={() => setShowTabErrorDialog(false)}
        title='Error pendiente'
        successButton={{
          title: 'Aceptar',
          action: () => setShowTabErrorDialog(false)
        }}
      >
        <Typography variant='body2'>
          {activeTab === 'weekly'
            ? 'Tiene un error pendiente por resolver en los horarios semanales antes de poder cambiar de pestaña.'
            : 'Tiene un error pendiente por resolver en las excepciones de recolección antes de poder cambiar de pestaña.'}
        </Typography>
      </Modal>
    </Fragment>
  )
})

CourierCollectionPlannerFormEdit.displayName = 'CourierCollectionPlannerFormEdit'
